import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Book, Code, Database, Shield, Zap } from 'lucide-react';

const DocumentationPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Documentation</h1>
          <p className="text-xl text-gray-600 mb-12">
            Everything you need to know about using TaskFlow effectively
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-lg shadow-md p-6">
              <Book className="w-10 h-10 text-primary-600 mb-4" />
              <h2 className="text-2xl font-semibold mb-4">Getting Started</h2>
              <ul className="space-y-3 text-gray-600">
                <li>• Create your account with email and password</li>
                <li>• Set up your profile and preferences</li>
                <li>• Create your first task and category</li>
                <li>• Organize tasks by priority and due dates</li>
                <li>• Track progress with status updates</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <Code className="w-10 h-10 text-primary-600 mb-4" />
              <h2 className="text-2xl font-semibold mb-4">API Reference</h2>
              <ul className="space-y-3 text-gray-600">
                <li>• RESTful API with JWT authentication</li>
                <li>• Complete CRUD operations for tasks</li>
                <li>• Category management endpoints</li>
                <li>• User profile and dashboard APIs</li>
                <li>• OpenAPI 3.0 specification available</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <Database className="w-10 h-10 text-primary-600 mb-4" />
              <h2 className="text-2xl font-semibold mb-4">Data Management</h2>
              <ul className="space-y-3 text-gray-600">
                <li>• Tasks are automatically saved</li>
                <li>• Categories help organize your work</li>
                <li>• Priority levels: Low, Medium, High</li>
                <li>• Status tracking: Todo, In Progress, Completed</li>
                <li>• Due dates with reminder notifications</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <Shield className="w-10 h-10 text-primary-600 mb-4" />
              <h2 className="text-2xl font-semibold mb-4">Security</h2>
              <ul className="space-y-3 text-gray-600">
                <li>• Secure JWT authentication</li>
                <li>• Encrypted password storage</li>
                <li>• Session management with 24-hour expiry</li>
                <li>• Input validation and sanitization</li>
                <li>• HTTPS encryption for all data</li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-6">Quick Start Guide</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">1. Registration</h3>
                <p className="text-gray-600">
                  Sign up with your email, username, and a secure password (minimum 6 characters).
                  Your account will be created instantly and you'll be logged in automatically.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">2. Creating Tasks</h3>
                <p className="text-gray-600">
                  Click the "New Task" button to create a task. Add a title, description, set priority,
                  choose categories, and optionally set a due date. Tasks appear immediately in your dashboard.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">3. Managing Tasks</h3>
                <p className="text-gray-600">
                  Click on any task to edit its details. Use the status dropdown to move tasks between
                  Todo, In Progress, and Completed. Delete tasks you no longer need with the delete button.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">4. Categories</h3>
                <p className="text-gray-600">
                  Create custom categories with colors to organize your tasks. Assign multiple categories
                  to a task for better organization. Filter tasks by category in the Tasks view.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-primary-50 rounded-lg p-6 text-center">
            <Zap className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Need More Help?</h3>
            <p className="text-gray-600 mb-4">
              Check out our API documentation or contact support for assistance.
            </p>
            <Link to="/contact" className="btn-primary inline-block">
              Contact Support
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DocumentationPage;
